import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Fade,
  Slide,
  useTheme,
  alpha,
  Autocomplete,
} from '@mui/material';
import {
  Close,
  Edit,
  Visibility,
  Download,
  Share,
  ContentCopy,
  CheckCircle,
  Info,
  Description,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { MedicalDataFile } from '../../types/medicalDataList';
import GlassCard from '../common/GlassCard';

interface FileActionModalProps {
  open: boolean;
  onClose: () => void;
  file: MedicalDataFile | null;
  mode: 'view' | 'edit';
  onSave?: (file: MedicalDataFile) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FileActionModal: React.FC<FileActionModalProps> = ({
  open,
  onClose,
  file,
  mode,
  onSave,
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [editedFile, setEditedFile] = useState<MedicalDataFile | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [availableTags] = useState(['医療', '診療', '予約', '患者', 'レポート', '分析', '東京院', '大阪院']);

  useEffect(() => {
    if (file) {
      setEditedFile({ ...file });
    }
  }, [file]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = () => {
    if (editedFile && onSave) {
      onSave(editedFile);
      onClose();
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const getStatusColor = (status: MedicalDataFile['status']) => {
    const colors = {
      uploaded: theme.palette.grey[500],
      processing: theme.palette.warning.main,
      completed: theme.palette.success.main,
      error: theme.palette.error.main,
    };
    return colors[status];
  };

  const renderEditContent = () => {
    if (!editedFile) return null;

    return (
      <Box>
        <TextField
          fullWidth
          label="ファイル名"
          value={editedFile.fileName}
          onChange={(e) => setEditedFile({ ...editedFile, fileName: e.target.value })}
          margin="normal"
          InputProps={{
            startAdornment: <Description sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />

        <Autocomplete
          multiple
          options={availableTags}
          value={editedFile.tags || []}
          onChange={(_, newValue) => setEditedFile({ ...editedFile, tags: newValue })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="タグ"
              margin="normal"
              placeholder="タグを選択または入力"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                sx={{ borderRadius: 1 }}
              />
            ))
          }
          freeSolo
        />

        <TextField
          fullWidth
          label="メモ"
          value={editedFile.notes || ''}
          onChange={(e) => setEditedFile({ ...editedFile, notes: e.target.value })}
          margin="normal"
          multiline
          rows={4}
          placeholder="ファイルに関するメモを入力..."
        />

        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            最終更新: {format(new Date(editedFile.lastModified || editedFile.uploadedAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderViewContent = () => {
    if (!file) return null;

    return (
      <Box>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="基本情報" icon={<Info />} iconPosition="start" />
          <Tab label="詳細情報" icon={<Description />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: '30%' }}>ファイル名</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {file.fileName}
                      <IconButton size="small" onClick={() => handleCopyToClipboard(file.fileName)}>
                        {copySuccess ? <CheckCircle fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ステータス</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        file.status === 'uploaded' ? 'アップロード済' :
                        file.status === 'processing' ? '処理中' :
                        file.status === 'completed' ? '完了' : 'エラー'
                      }
                      size="small"
                      sx={{
                        backgroundColor: alpha(getStatusColor(file.status), 0.1),
                        color: getStatusColor(file.status),
                        fontWeight: 600,
                      }}
                    />
                    {file.processingProgress && (
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        ({file.processingProgress}%)
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>アップロード日時</TableCell>
                  <TableCell>{format(new Date(file.uploadedAt), 'yyyy年MM月dd日 HH:mm', { locale: ja })}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>アップロード者</TableCell>
                  <TableCell>{file.uploadedBy || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>ファイルサイズ</TableCell>
                  <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>レコード数</TableCell>
                  <TableCell>{file.recordCount.toLocaleString()} 件</TableCell>
                </TableRow>
                {file.tags && file.tags.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>タグ</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {file.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <GlassCard sx={{ p: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              ファイルID
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
              {file.id}
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              最終更新日時
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {format(new Date(file.lastModified || file.uploadedAt), 'yyyy年MM月dd日 HH:mm:ss', { locale: ja })}
            </Typography>

            {file.notes && (
              <>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  メモ
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {file.notes}
                </Typography>
              </>
            )}

            <Divider sx={{ my: 3 }} />

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                ダウンロード
              </Button>
              <Button
                variant="outlined"
                startIcon={<Share />}
                size="small"
                sx={{
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                共有
              </Button>
            </Stack>
          </GlassCard>
        </TabPanel>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Slide}
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.98),
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'view' ? (
            <>
              <Visibility color="primary" />
              <Typography variant="h6">ファイル詳細</Typography>
            </>
          ) : (
            <>
              <Edit color="primary" />
              <Typography variant="h6">ファイル編集</Typography>
            </>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {mode === 'edit' ? renderEditContent() : renderViewContent()}
          </motion.div>
        </AnimatePresence>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          キャンセル
        </Button>
        {mode === 'edit' && (
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            保存
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FileActionModal;